import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import WikiBreadcrumbs from './components/WikiBreadcrumbs';
import WikiCategoryGrid, { CategoryItem } from './components/WikiCategoryGrid';
import WikiSearch, { WikiSearchScope } from './components/WikiSearch';
import { parseWikiUrl, buildWikiUrl } from '../../utils/wikiUrlParser';
import { useWikiTypes } from '../../contexts/WikiTypesContext';
import GlassContainer from '../../components/layout/GlassContainer';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import { resolveApiSource } from '../../utils/wikiSourceResolver';
import { flattenProfessionsWithDepth, buildProfessionHierarchyMap } from '../../utils/professionHierarchy';
import { SCROLL_RESERVE } from '../../utils/responsive';

/**
 * Страница визуальной навигации - показывает карточки для перехода на следующий уровень иерархии.
 * Используется когда текущий уровень имеет дочерние категории (не листовой узел).
 */
const WikiVisualNavigationPage: React.FC = () => {
  const location = useLocation();
  const { categories } = useWikiTypes();
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));

  const urlParams = parseWikiUrl(location.pathname);

  // Поиск показываем только на ВЕРХНЕМ уровне трёх главных категорий (Предметы/Классы/НПЦ).
  // На top-level URL-source уже равен 'items'|'skills'|'npcs' (resolveApiSource: items→items, classes→skills, npc→npcs),
  // и это же значение — параметр scope для /wiki/search.
  const topLevelScope = useMemo((): WikiSearchScope | null => {
    if (!urlParams) return null;
    const { source, subcategoryId, typeId, subtypeId, raceId, skillTypeId, classId, professionId } = urlParams;
    const isTopLevel = !subcategoryId && !typeId && !subtypeId && !raceId && !skillTypeId && !classId && !professionId;
    if (!isTopLevel) return null;
    return source === 'items' || source === 'skills' || source === 'npcs' ? source : null;
  }, [urlParams]);

  // Получаем карточки для текущего уровня
  const categoryItems = useMemo((): CategoryItem[] => {
    if (!urlParams) {
      return [];
    }

    const { source, subcategoryId, typeId, raceId, skillTypeId, classId } = urlParams;

    // Находим категорию по resolved API source (как sidebar строит URL)
    let category: typeof categories[0] | undefined;
    for (const cat of categories) {
      // Проверяем resolved API source категории
      if (resolveApiSource([cat.source]) === source) {
        category = cat;
        break;
      }
      // Проверяем resolved API source подкатегорий
      for (const subCat of cat.subCategories || []) {
        if (resolveApiSource([cat.source, subCat.source]) === source) {
          category = cat;
          break;
        }
        // Проверяем типы подкатегорий
        for (const type of subCat.types || []) {
          if (resolveApiSource([cat.source, subCat.source, type.source]) === source) {
            category = cat;
            break;
          }
        }
        if (category) break;
      }
      if (category) break;
    }

    if (!category) return [];

    // Получаем resolved API source для URL
    const apiSource = resolveApiSource([category.source]);

    // === SKILLS иерархия ===
    if (category.races && category.races.length > 0) {
      // Показываем профессии
      if (classId && skillTypeId && raceId) {
        for (const race of category.races) {
          if (race.id === raceId) {
            for (const skillType of race.skillTypes || []) {
              if (skillType.id === skillTypeId) {
                for (const classItem of skillType.classes || []) {
                  if (classItem.id === classId) {
                    const professions = classItem.professions || [];
                    const flatProfessions = flattenProfessionsWithDepth(professions);
                    const hierarchyMap = buildProfessionHierarchyMap(professions);

                    return flatProfessions.map(({ profession }) => {
                      const hierarchy = hierarchyMap.get(profession.id);
                      return {
                        id: profession.id,
                        name: profession.name,
                        source: profession.source || 'profession',
                        path: buildWikiUrl({
                          source: apiSource,
                          raceId,
                          skillTypeId,
                          classId,
                          professionId: profession.id,
                        }),
                        ...(hierarchy && {
                          hierarchyColor: hierarchy.color,
                          hierarchyDepth: hierarchy.depth,
                          hierarchyHasChildren: hierarchy.hasChildren,
                          hierarchyIsRoot: hierarchy.isRoot,
                        }),
                      };
                    });
                  }
                }
              }
            }
          }
        }
      }

      // Показываем классы
      if (skillTypeId && raceId) {
        for (const race of category.races) {
          if (race.id === raceId) {
            for (const skillType of race.skillTypes || []) {
              if (skillType.id === skillTypeId) {
                return (skillType.classes || []).map(cls => ({
                  id: cls.id,
                  name: cls.name,
                  source: cls.source || 'class',
                  path: buildWikiUrl({
                    source: apiSource,
                    raceId,
                    skillTypeId,
                    classId: cls.id,
                  }),
                }));
              }
            }
          }
        }
      }

      // Показываем типы скиллов
      if (raceId) {
        for (const race of category.races) {
          if (race.id === raceId) {
            return (race.skillTypes || []).map(st => ({
              id: st.id,
              name: st.name,
              source: st.source || 'skilltype',
              path: buildWikiUrl({
                source: apiSource,
                raceId,
                skillTypeId: st.id,
              }),
            }));
          }
        }
      }

      // Показываем расы
      return (category.races || []).map(race => ({
        id: race.id,
        name: race.name,
        source: race.source || 'race',
        path: buildWikiUrl({
          source: apiSource,
          raceId: race.id,
        }),
      }));
    }

    // === Стандартная иерархия (weapons, armors, items, npcs) ===

    // Показываем подтипы
    if (typeId && subcategoryId) {
      for (const subCat of category.subCategories || []) {
        if (subCat.id === subcategoryId) {
          for (const type of subCat.types || []) {
            if (type.id === typeId) {
              // Resolve API source from full hierarchy
              const subTypeApiSource = resolveApiSource([category.source, subCat.source, type.source]);
              return (type.subTypes || []).map(st => ({
                id: st.id,
                name: st.name,
                source: st.source || 'subtype',
                path: buildWikiUrl({
                  source: subTypeApiSource,
                  subcategoryId,
                  typeId,
                  subtypeId: st.id,
                }),
              }));
            }
          }
        }
      }
    }

    // Показываем типы
    if (subcategoryId) {
      for (const subCat of category.subCategories || []) {
        if (subCat.id === subcategoryId) {
          // Resolve API source from hierarchy
          const typeApiSource = resolveApiSource([category.source, subCat.source]);
          return (subCat.types || []).map(type => ({
            id: type.id,
            name: type.name,
            source: type.source || 'type',
            path: buildWikiUrl({
              source: typeApiSource,
              subcategoryId,
              typeId: type.id,
            }),
          }));
        }
      }
    }

    // Показываем подкатегории
    return (category.subCategories || []).map(subCat => {
      const subCatApiSource = resolveApiSource([category!.source, subCat.source]);
      return {
        id: subCat.id,
        name: subCat.name,
        source: subCat.source,
        path: buildWikiUrl({
          source: subCatApiSource,
          subcategoryId: subCat.id,
        }),
      };
    });
  }, [categories, urlParams]);

  return (
    <GlassContainer sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {topLevelScope ? (
        // Верхний уровень главной категории — с поиском (scoped по этой группе)
        <WikiSearch source={topLevelScope} breadcrumbs={<WikiBreadcrumbs />}>
          <WikiCategoryGrid categories={categoryItems} />
        </WikiSearch>
      ) : (
        // Промежуточные уровни — просто карточки навигации
        <Box sx={{ color: 'white', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <WikiBreadcrumbs />
          {/* Резерв справа: с sm — под полосу скролла; на мобиле полосы нет, но пара пикселей
              нужна, чтобы дробный край карточки не попадал на линию обрезки SimpleBar (см. WikiSearch) */}
          <SimpleBar
            style={{ flex: 1, minHeight: 0, paddingRight: isSm ? SCROLL_RESERVE.sm : SCROLL_RESERVE.xs }}
            autoHide={false}
          >
            {/* Зазор от крошек до сетки — на уровне страницы (у сетки pt:0), единое соглашение */}
            <Box sx={{ pt: { xs: 1, sm: 2 } }}>
              <WikiCategoryGrid categories={categoryItems} />
            </Box>
          </SimpleBar>
        </Box>
      )}
    </GlassContainer>
  );
};

export default WikiVisualNavigationPage;
